import { createSelector, createStructuredSelector } from 'reselect';
import { initialState } from './reducer';
import { JarvisHomebox, RootState } from '../../interface';
import { Namespace } from '../../enum';
import { AppState } from './interface';
import { ErrorType } from './enum';

/**
 * Get the selected router from state based on availability and selected id
 * @param {AppState} state App's state
 * @returns {JarvisHomebox|undefined} Selected product or undefined if not available.
 */
export const getSelectedRouter = (
    { routers, selectedId, providerType }: AppState
): JarvisHomebox | undefined =>
    routers[providerType].find(({ id }) => id === selectedId);

export const selectState = (state: RootState) => state[Namespace.APP] || initialState;

export const selectLoading = createSelector(selectState, state => state.isLoading);
export const selectSelectedId = createSelector(selectState, state => state.selectedId);
export const selectDisableBooking = createSelector(selectState, state => state.disableBooking);
export const selectPublicFactName = createSelector(selectState, state => state.publicFactName);
export const selectCustomerType = createSelector(selectState, state => state.customerType);
export const selectRouters = createSelector(selectState, state => state.routers[state.providerType]);
export const selectRouterIds = createSelector(selectState, state =>
    state.routers[state.providerType].map(router => router.id));
export const selectAvailableRouterIds = createSelector(selectState, state => state.availableRouterIds);
export const selectRouter = createSelector(selectState, getSelectedRouter);
export const selectIsLoggedIn = createSelector(selectState, state => Boolean(state.customer));
export const selectCustomer = createSelector(selectState, state => state.customer);
export const selectIsAvcError = createSelector(selectState, state =>
    !!state.error && state.error.type === ErrorType.AVC);
export const selectIsCmsError = createSelector(selectState, state =>
    !!state.error && state.error.type === ErrorType.CMS_CONFIG);
export const selectIsJarvisError = createSelector(selectState, state =>
    !!state.error && state.error.type === ErrorType.JARVIS);
export const selectIsContentEnabled = createSelector(selectState,
    ({ customer, availableRouterIds }) => !customer || Boolean(availableRouterIds.length));

export const mapStateToProps = createStructuredSelector({
    isLoading: selectLoading,
    selectedId: selectSelectedId,
    routers: selectRouters,
    routerIds: selectRouterIds,
    availableRouterIds: selectAvailableRouterIds,
    isLoggedIn: selectIsLoggedIn,
    customer: selectCustomer,
    isContentEnabled: selectIsContentEnabled,
    isAvcError: selectIsAvcError,
    isCmsError: selectIsCmsError,
    isJarvisError: selectIsJarvisError,
    disableBooking: selectDisableBooking,
    publicFactName: selectPublicFactName,
    customerType: selectCustomerType,
});

export type State = ReturnType<typeof mapStateToProps>;
